import * as React from "react";
import { Check, Copy, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useToastAppearance } from "@/hooks/useToastAppearance";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { FEATURE_FLAG_KEYS } from "@/lib/featureFlagsCatalog";
import { useOrganization } from "@/contexts/OrganizationContext";
import { LegacyToaster } from "@/components/ui/toaster-legacy";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastIcon,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastVariantInput,
} from "@/components/ui/toast";

function flattenToastContent(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenToastContent).filter(Boolean).join(" ");
  if (React.isValidElement(node)) return flattenToastContent(node.props.children);
  return "";
}

function ToastCopyButton({ title, description }: { title?: React.ReactNode; description?: React.ReactNode }) {
  const [copied, setCopied] = React.useState(false);
  const [isCopying, setIsCopying] = React.useState(false);

  const handleCopy = async () => {
    const text = [flattenToastContent(title), flattenToastContent(description)].filter(Boolean).join("\n");
    if (!text) return;

    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } finally {
      setIsCopying(false);
    }
  };

  if (!title && !description) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={isCopying}
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50",
        "text-foreground/60 hover:bg-muted hover:text-foreground",
        "group-data-[toast-style=aurora]:text-white/75 group-data-[toast-style=aurora]:hover:bg-white/15 group-data-[toast-style=aurora]:hover:text-white",
      )}
      aria-label="Copy toast details"
    >
      {isCopying ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

export function ModernToaster({ userId }: { userId?: string }) {
  const { toasts } = useToast();
  const { appearance } = useToastAppearance(userId);

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const toastVariant = variant as ToastVariantInput | undefined;
        return (
          <Toast
            key={id}
            variant={toastVariant}
            surfaceStyle={appearance.style}
            translucent={appearance.translucent}
            {...props}
          >
            <ToastIcon variant={toastVariant} surfaceStyle={appearance.style} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
              {action ? <div className="flex flex-wrap items-center gap-2 pt-1.5">{action}</div> : null}
            </div>
            <div className="absolute right-2.5 top-2.5 flex items-center gap-0.5">
              <ToastCopyButton title={title} description={description} />
              <ToastClose />
            </div>
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}

// `modern_toast` is an all-users (global) flag, but `useFeatureFlag` can only resolve it
// for an authenticated user — anonymous surfaces (/auth, post sign-out) have no userId, so
// the query is disabled and would otherwise be pinned to `defaultEnabled`, meaning a
// super-admin rollback (flag OFF) could never reach auth-page toasts. We cache the last
// resolved value on the device and use it as the anonymous default, so the kill switch still
// applies app-wide (ON until the first resolution on a fresh device).
const MODERN_TOAST_CACHE_KEY = 'modern_toast_enabled';

function readCachedModernToast(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const value = window.localStorage.getItem(MODERN_TOAST_CACHE_KEY);
    return value === null ? true : value === 'true';
  } catch {
    return true;
  }
}

function writeCachedModernToast(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MODERN_TOAST_CACHE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal */
  }
}

/**
 * Mounted once at the app root. Gates the modern toast behind the `modern_toast`
 * feature flag (default ON — seeded enabled for all users). When a super admin
 * turns the flag OFF, the app reverts to the pre-ENT-1956 {@link LegacyToaster}.
 * Anonymous users fall back to the last flag value cached on this device so the
 * kill switch reaches pre-auth surfaces too (see cache note above).
 */
export function Toaster() {
  const { user } = useOrganization();
  const [cachedDefault, setCachedDefault] = React.useState(readCachedModernToast);
  const { enabled: modernToastEnabled, flag } = useFeatureFlag(FEATURE_FLAG_KEYS.modernToast, {
    userId: user?.id,
    defaultEnabled: cachedDefault,
  });

  React.useEffect(() => {
    if (flag) {
      writeCachedModernToast(flag.enabled);
      setCachedDefault(flag.enabled);
    }
  }, [flag]);

  return modernToastEnabled ? <ModernToaster userId={user?.id} /> : <LegacyToaster />;
}
